import React from "react";
import { isUndefined } from "lodash";
import { useFormContext } from "react-hook-form";
import { ParcelImportFrequency } from "../../../../../generated/graphql";
import { spacing } from "../../../../../stitches.config";
import { Section, Sections } from "../../../../Common/Layout";
import { Body, Title } from "../../../../Common/Typography";
import { Password, Select, Text } from "../../../../Inputs/react-hook-form";
import { ConfigStageWrapper } from "./__styles__/ParcelImportWizard";
import { ArcGISImportWizardFormData } from "./types";

const AuthorizationConfig = () => {
  const {
    formState: { errors },
    register,
  } = useFormContext<ArcGISImportWizardFormData>();

  return (
    <div>
      <Title size="small" type="semiBold" color="contentPrimary">
        Authentication
      </Title>
      <Sections gap={spacing.m}>
        <Body size="default" type="regular" color="contentSecondary">
          Enter username, password, and portal URL below if the service requires
          authentication
        </Body>
        <Section>
          <Text
            label="Username"
            error={errors.username?.message}
            {...register("username")}
          />
        </Section>
        <Section>
          <Password
            label="Password"
            error={errors.password?.message}
            {...register("password")}
          />
        </Section>
        <Section>
          <Text
            label="ArcGIS portal URL"
            error={errors.arcgisPortal?.message}
            {...register("arcgisPortal")}
          />
        </Section>
      </Sections>
    </div>
  );
};

const ArcgisBasicConfig = () => {
  const {
    control,
    formState: { errors },
    register,
  } = useFormContext<ArcGISImportWizardFormData>();

  return (
    <Sections gap={spacing.m}>
      <Section>
        <Text
          label="Name"
          error={errors.name?.message}
          required
          {...register("name", {
            required: "Name is required",
          })}
        />
      </Section>
      <Section>
        <Text
          placeholder="https://"
          label="URL"
          error={errors.url?.message}
          description="Layer ID required in URL (typically ending in “.../MapServer/#” or “...FeatureServer/#”)"
          required
          {...register("url", {
            required: "Connection url is required",
            validate: async (val: string) => {
              try {
                new URL(val);
              } catch (_) {
                return "The url provided is malformed.";
              }
              return;
            },
          })}
        />
      </Section>
      <Section>
        <Select
          control={control}
          name={"frequency"}
          label="Update frequency"
          required
          rules={{
            validate: val => {
              if (isUndefined(val)) {
                return "Frequency is required";
              }

              return true;
            },
          }}
          error={errors.frequency?.message}
          options={[
            { value: null, label: "Only once" },
            { value: ParcelImportFrequency.DAILY, label: "Every day" },
            { value: ParcelImportFrequency.WEEKLY, label: "Every week" },
            {
              value: ParcelImportFrequency.BIWEEKLY,
              label: "Every other week",
            },
            { value: ParcelImportFrequency.MONTHLY, label: "Every month" },
          ]}
        />
      </Section>
    </Sections>
  );
};

const ArcGISParcelImportConfigStage = () => {
  return (
    <ConfigStageWrapper>
      <Sections gap={spacing.l}>
        <ArcgisBasicConfig />
        <AuthorizationConfig />
      </Sections>
    </ConfigStageWrapper>
  );
};

export default ArcGISParcelImportConfigStage;
